import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { endpointAPI } from '../../services/api';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { toast } from 'react-hot-toast';

interface Policy {
  scan_schedule: string;
  usb_scan_enabled: boolean;
  usb_block_enabled: boolean;
  background_scan_enabled: boolean;
  usage_tracking_enabled: boolean;
  usage_capture_urls: boolean;
}

const EndpointPolicyPage: React.FC = () => {
  useDocumentTitle('Endpoint Policy');
  const navigate = useNavigate();
  const [policy, setPolicy] = useState<Policy | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  const load = async () => {
    try {
      const res = await endpointAPI.getPolicy();
      if (res.data.status === 'success') setPolicy(res.data.data);
    } catch {
      toast.error('Failed to load policy');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (key: keyof Policy) => {
    if (!policy || saving) return;
    const next = { ...policy, [key]: !policy[key] } as Policy;
    setPolicy(next); // optimistic
    setSaving(key);
    try {
      await endpointAPI.setPolicy({ [key]: next[key] as boolean });
      toast.success('Policy updated');
    } catch {
      setPolicy(policy); // revert
      toast.error('Failed to update policy');
    } finally {
      setSaving(null);
    }
  };

  const setSchedule = async (value: string) => {
    if (!policy) return;
    setPolicy({ ...policy, scan_schedule: value });
    try {
      await endpointAPI.setPolicy({ scan_schedule: value });
      toast.success('Scan schedule updated');
    } catch { toast.error('Failed to update schedule'); }
  };

  const Switch: React.FC<{ label: string; desc: string; k: keyof Policy; danger?: boolean }> = ({ label, desc, k, danger }) => (
    <div className={`d-flex justify-content-between align-items-start p-3 border rounded mb-3 ${policy && policy[k] ? (danger ? 'border-warning' : 'border-primary') : ''}`}>
      <div className="pe-3">
        <div className="fw-semibold">{label}</div>
        <div className="small text-muted">{desc}</div>
      </div>
      <div className="form-check form-switch ms-2">
        <input className="form-check-input" type="checkbox" role="switch"
          checked={!!(policy && policy[k])} disabled={saving === k}
          onChange={() => toggle(k)} style={{ cursor: 'pointer' }} />
      </div>
    </div>
  );

  if (loading || !policy) {
    return <DashboardLayout><div className="text-center py-5"><div className="spinner-border text-primary"></div></div></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <div className="container py-3" style={{ maxWidth: 780 }}>
        <button className="btn btn-link px-0 mb-2" onClick={() => navigate('/endpoints')}>
          <i className="bi bi-arrow-left me-1"></i>Back to Endpoints
        </button>
        <h3 className="mb-1"><i className="bi bi-sliders me-2"></i>Endpoint Policy</h3>
        <p className="text-muted">These settings apply to all endpoints in your organisation.</p>

        <div className="card mb-4">
          <div className="card-header"><i className="bi bi-shield-check me-2"></i>Scanning</div>
          <div className="card-body">
            <Switch k="background_scan_enabled" label="Background scanning"
              desc="Run a daily signature scan of user folders (Downloads, Desktop, Documents)." />
            <Switch k="usb_scan_enabled" label="USB scanning"
              desc="Automatically scan removable media as soon as it's inserted." />
            <Switch k="usb_block_enabled" label="Block USB on threat" danger
              desc="Quarantine flagged files found on removable media automatically." />
            <div className="mt-3">
              <label className="form-label small text-muted text-uppercase">Scan schedule</label>
              <select className="form-select" value={policy.scan_schedule} onChange={e => setSchedule(e.target.value)}>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="manual">Manual only</option>
              </select>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header"><i className="bi bi-eye me-2"></i>Usage Monitoring</div>
          <div className="card-body">
            <div className="alert alert-warning small">
              <i className="bi bi-exclamation-triangle me-2"></i>
              <strong>Employee monitoring notice.</strong> App and browsing usage tracking is off by default.
              In many regions you must inform employees (and sometimes obtain consent) before enabling it.
              You are responsible for complying with local law where your endpoints operate.
            </div>
            <Switch k="usage_tracking_enabled" label="Track app usage" danger
              desc="Record which applications are active and for how long, aggregated per app." />
            <Switch k="usage_capture_urls" label="Capture site/URL hosts" danger
              desc="Also record the website hosts visited in browsers. Requires app usage tracking to be on." />
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default EndpointPolicyPage;
